import { Icons } from '@/components/icons';
import LegCard from '@/components/manage/tickets/LegCard';
import { Typography } from '@/components/primitives/Typography';
import { manageFlowBookingAdmissionsByIdSelector } from '@/features/manage/manageSelectors';
import { useBackgroundTheme } from '@/hooks/useBackgroundTheme';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type { BookedTripDTO } from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { isLightBackground } from '@/utils/color';
import { type FC, type ReactNode, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';

interface TripsByDirectionProps {
  trips: Array<BookedTripDTO>;
  passengersMap: CheckoutOrBookingPassengerMap;
  header: ReactNode;
}

const TripsByDirection: FC<TripsByDirectionProps> = ({
  trips,
  header,
  passengersMap,
}) => {
  const backgroundTheme = useBackgroundTheme();
  const isLight = isLightBackground(backgroundTheme);
  const extractPassengerIds = useCallback((trip: BookedTripDTO): string[] => {
    const passengerIds = (trip.bookedOffers || [])
      .flatMap((offer) => offer.admissions || [])
      .flatMap((admission) => admission.passengerIds || []);

    return [...new Set(passengerIds)].sort();
  }, []);

  const groupTripsByPassenger = useCallback(
    (trips: Array<BookedTripDTO>) => {
      const groupsMap = new Map<
        string,
        { passengerIds: Array<string>; trips: Array<BookedTripDTO> }
      >();

      trips.forEach((trip) => {
        const passengerIds = extractPassengerIds(trip);
        const groupKey = passengerIds.join(',');

        const existingGroup = groupsMap.get(groupKey);
        if (existingGroup) {
          existingGroup.trips.push(trip);
        } else {
          groupsMap.set(groupKey, { passengerIds, trips: [trip] });
        }
      });

      return Array.from(groupsMap.values());
    },
    [extractPassengerIds]
  );

  const admissionsByLegId = useSelector(
    manageFlowBookingAdmissionsByIdSelector
  );
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const grouped = groupTripsByPassenger(trips);
  const showTripHeadings = grouped.length > 1;

  return (
    <div className="flex flex-col">
      <div
        className={cn('mb-4 flex flex-row items-baseline gap-4', {
          'flex-col gap-1': !isLaptopOrBigger,
        })}
      >
        <Typography
          variant={isLaptopOrBigger ? 'heading2' : 'subtitle'}
          asChild
        >
          <h1>{header}</h1>
        </Typography>
      </div>
      {grouped.map((group, groupIdx) => {
        const uniqueODPairs = Array.from(
          new Map(
            group.trips.map((trip) => [
              JSON.stringify([
                trip.originStop?.code || trip.originStop?.name,
                trip.destinationStop?.code || trip.destinationStop?.name,
              ]),
              trip,
            ])
          ).values()
        );
        return (
          <div key={groupIdx} className="mb-6">
            {showTripHeadings && (
              <Typography
                variant={isLaptopOrBigger ? 'subtitle' : 'body1-bold'}
                className="mb-2"
                asChild
              >
                <h2>
                  <TransText
                    i18nKey="tripNumber"
                    values={{ number: groupIdx + 1 }}
                  />
                </h2>
              </Typography>
            )}
            {uniqueODPairs.map((trip, key) => {
              const fulfilledCount = (() => {
                const fulfilledPassengerIds = (trip.bookedOffers || [])
                  .flatMap((offer) => offer.admissions || [])
                  .filter((admission) =>
                    (admission.fulfillments || []).some(
                      (f) => f.status === 'FULFILLED'
                    )
                  )
                  .flatMap((admission) => admission.passengerIds || []);
                return Array.from(new Set(fulfilledPassengerIds)).length;
              })();

              return (
                <div key={key}>
                  <Typography
                    variant={isLaptopOrBigger ? 'paragraph' : 'body2'}
                    className={cn(
                      'mb-2 flex flex-row items-center gap-1 text-neutral-light',
                      { 'text-neutral': isLight }
                    )}
                  >
                    {trip.originStop?.name}
                    <Icons.oneWay height={20} width={20} />
                    {trip.destinationStop?.name}
                    {fulfilledCount > 0 && (
                      <>
                        <Icons.dot className="mx-1.5" />
                        <TransText
                          i18nKey="passengersCount"
                          values={{
                            count: fulfilledCount,
                          }}
                        />
                      </>
                    )}
                  </Typography>
                  {trip.legs?.map((leg, index) => {
                    const nextLeg = trip.legs?.[index + 1];
                    const admissions = leg.id ? admissionsByLegId[leg.id] : [];

                    return (
                      <LegCard
                        key={index}
                        leg={leg}
                        nextLeg={nextLeg}
                        admissions={admissions}
                        passengersMap={passengersMap}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default TripsByDirection;
